import React, {useCallback, useEffect, useRef, useState} from "react";
import {Button, Checkbox, Col, Input, PageHeader, Row, Select, Table, Tooltip,} from "antd";
import {
  CheckCircleTwoTone,
  ClockCircleTwoTone,
  CloseCircleTwoTone,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import moment from "moment";
import _ from "lodash";
import {getEmployees, upsertEmployee, getEmployeeManageData} from "../api/employeeApi";
import {OnboardButton} from "./OnboardButton";
import {RemoveButton} from "./RemoveButton";
import {useUserPermissions} from "../auth/userHooks";
import {PREFERRED_DATE_FORMAT} from "../util/time";
import EmployeeFullInfoModal from "./EmployeeFullInfoModal";
import EditButton from "./EditButton";
import CreateUpdateEmployeeModal from "./CreateUpdateEmployeeModal";
import {getPaginationConfig} from "../util/showAllPagination";
import {useDynamicTableHeight} from "../util/useDynamicTableHeight";
import sorters from "../util/sorters";
import {getPracticeAbbrev} from "../util/practiceAbbrev";
import {PSA_INSTANCE} from "./Constants";

const Employees = () => {
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [showNotSyncOnly, setShowNotSyncOnly] = useState(true);
  const [showFutureEmployeesOnly, setShowFutureEmployeesOnly] = useState(true);
  const [showContractorRates, setShowContractorRates] = useState(false);
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editEmployee, setEditEmployee] = useState(null);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const permissions = useUserPermissions();
  const tableContainerRef = useRef(null);
  const tableHeight = useDynamicTableHeight(tableContainerRef);
  const [selectedPsaInstances, setSelectedPsaInstances] = useState([]);

  const [manageData, setManageData] = useState({
    capabilities: [],
    employees: [],
    employment_status: [],
    levels: [],
    locations: [],
    practices: [],
    titles: [],
    managers: []
  });

  const openEditModal = (employee) => {
    setEditEmployee(employee);
    setEditModalVisible(true);
  };

  const closeEditModal = () => {
    setEditModalVisible(false);
    setEditEmployee(null);
  };

  const handleUpsertEmployee = async (fields) => {
    try {
      const payload = {
        id: editEmployee ? editEmployee.id : null,
        effectiveDate: fields.effectiveDate ? fields.effectiveDate.format("YYYY-MM-DD") : null,
        firstName: fields.firstName,
        lastName: fields.lastName,
        email: fields.email,
        title: fields.title,
        level: fields.level,
        practiceName: fields.practiceName,
        capability: fields.capability,
        locationName: fields.locationName,
        employment_status: fields.employment_status,
        contractorRate: fields.contractorRate,
        managerId: fields.managerId,
        careerCoachId: fields.careerCoachId,
        startDate: fields.startDate ? fields.startDate.format("YYYY-MM-DD") : null,
        termDate: fields.termDate ? fields.termDate.format("YYYY-MM-DD") : null,
        ptoPolicy: fields.ptoPolicy,
        ptoModifier: fields.ptoModifier,
        ptoHoursOverride: fields.ptoHoursOverride,
        psaInstances: fields.psaInstances ? fields.psaInstances : [],
        primaryPsaInstance: fields.primaryPsaInstance
      };
      await upsertEmployee(payload);
      await loadEmployees();
      if (editEmployee) {
        closeEditModal();
      } else {
        setCreateModalVisible(false);
      }
    } catch (e) {
      throw e;
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "fullName",
      width: "11%",
      ellipsis: true,
      sorter: (a, b) => sorters.string(a.fullName, b.fullName),
    },
    {
      title: "Email",
      dataIndex: "email",
      width: "16%",
      ellipsis: true,
      sorter: (a, b) => sorters.string(a.email, b.email),
    },
    {
      title: "Title",
      dataIndex: "title",
      width: "13%",
      ellipsis: true,
      sorter: (a, b) => sorters.string(a.title, b.title),
    },
    {
      title: "Level",
      dataIndex: "level",
      width: 60,
      align: "right",
      ellipsis: true,
      sorter: (a, b) => (a.level || 0) - (b.level || 0),
      render: (level) => level !== null && level !== undefined ? level : "-",
    },
    {
      title: "Practice",
      dataIndex: "practiceName",
      width: 60,
      ellipsis: true,
      sorter: (a, b) => sorters.string(a.practiceName, b.practiceName),
      render: (practiceName) => getPracticeAbbrev(practiceName),
    },
    {
      title: "Capability",
      dataIndex: "capability",
      width: "10%",
      ellipsis: true,
      sorter: (a, b) => sorters.string(a.capability, b.capability),
    },
    {
      title: "Location",
      dataIndex: "locationName",
      width: "9%",
      ellipsis: true,
      sorter: (a, b) => sorters.string(a.locationName, b.locationName),
    },
    {
      title: "Contractor Rate",
      dataIndex: "contractorRate",
      sorter: (a, b) => a.contractorRate - b.contractorRate,
      width: 85,
      align: "right",
      ellipsis: true,
      render: (r) => (r ? `$${r.toFixed(2)}` : "-"),
      hidden: !showContractorRates,
    },
    {
      title: "PTO Policy",
      dataIndex: "ptoPolicy",
      width: "8%",
      ellipsis: true,
      sorter: (a, b) => sorters.string(a.ptoPolicy, b.ptoPolicy),
      render: (policy, record) => {
        const modifier = record.ptoModifier;
        const hoursOverride = record.ptoHoursOverride;

        if (!policy && !modifier && !hoursOverride) return "-";

        let displayText = policy || "-";

        if (modifier) {
          displayText += ` (${modifier})`;
        }

        if (hoursOverride) {
          displayText += ` [Override: ${hoursOverride}h]`;
        }

        return displayText;
      },
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      width: 100,
      defaultSortOrder: "ascend",
      sorter: (a, b) =>
        sorters.date(a.startDate, b.startDate),
      render: (startDate) =>
        moment.utc(startDate)
          .format(PREFERRED_DATE_FORMAT),
    },
    {
      title: 'Term Date',
      dataIndex: "termDate",
      width: 100,
      sorter: (a, b) => sorters.date(a.termDate, b.termDate),
      render: (termDate) => termDate ?
        moment.utc(termDate)
          .format(PREFERRED_DATE_FORMAT) : "",
    },
    {
      title: 'Employment Status',
      dataIndex: "employment_status",
      width: "10%",
      sorter: (a, b) => sorters.string(a.employment_status, b.employment_status),
    },
    {
      title: "Status",
      width: 60,
      render: (text, record) => (
        <>
          {getStatusIcon(record, "SCOUT")}
          &nbsp;
          {getStatusIcon(record, "PROJECTOR")}
        </>
      ),
    },
    {
      title: "Action",
      width: 115,
      key: "action",
      onCell: () => {
        return {
          onClick: (event) => {
            event.stopPropagation();
          },
        };
      },
      render: (text, record) => (
        <div style={{whiteSpace: "nowrap"}}>
          {permissions !== null && permissions.canManageEmployees && (
            <>
              <EditButton onClick={() => openEditModal(record)}/>
              &nbsp;
              <OnboardButton employee={record}/>
              &nbsp;
              <RemoveButton employee={record}/>
            </>
          )}
        </div>
      ),
    },
  ].filter((item) => !item.hidden);

  const getStatus = (record, statusType) => {
    return (
      _.chain(record.statusHistory).filter({statusType}).first().value() || {
        status: "NOT_STARTED",
      }
    );
  };

  const capitalize = (str) => {
    return _.startCase(_.lowerCase(str));
  };

  const getStatusIcon = (record, statusType) => {
    const latestStatus = getStatus(record, statusType);

    let icon;

    switch (latestStatus.status) {
      case "STARTED":
        icon = <ClockCircleTwoTone twoToneColor="#53AEDE"/>;
        break;
      case "COMPLETED":
        icon = <CheckCircleTwoTone twoToneColor="#52C41A"/>;
        break;
      case "FAILED":
        icon = <CloseCircleTwoTone twoToneColor="#FF2800"/>;
        break;
      case "NOT_STARTED":
      default:
        icon = <CheckCircleTwoTone twoToneColor="#CCC"/>;
    }

    return !_.isEmpty(latestStatus.comment) ? (
      <Tooltip
        title={`${capitalize(statusType)} - ${capitalize(
          latestStatus.status
        )} (${latestStatus.comment})`}
      >
        {icon}
      </Tooltip>
    ) : (
      <Tooltip
        title={`${capitalize(statusType)} - ${capitalize(latestStatus.status)}`}
      >
        {icon}
      </Tooltip>
    );
  };

  const isSynchronized = useCallback((record) => {
    const statuses = [
      getStatus(record, "SCOUT"),
      getStatus(record, "PROJECTOR"),
    ];

    return _.every(statuses, (s) => s.status === "COMPLETED");
  }, []);

  const isFutureEmployee = (record) => {
    return moment(record.startDate).isAfter(moment());
  };

  const isActiveEmployee = (record) => {
    return record.employment_status !== "terminated";
  }

  const filterEmployees = useCallback((
    employees,
    searchText,
    showNotSyncOnly,
    showFutureEmployeesOnly,
    showActiveOnly,
    selectedPsaInstances
  ) => {
    setFilteredEmployees(
      _.chain(employees)
        .filter(
          (u) =>
            searchText === "" ||
            _.includes(_.toLower(u.fullName), _.toLower(searchText)) ||
            _.includes(_.toLower(u.email), _.toLower(searchText)) ||
            _.includes(_.toLower(u.title), _.toLower(searchText))
        )
        .filter((u) => !showNotSyncOnly || !isSynchronized(u))
        .filter((u) => !showFutureEmployeesOnly || isFutureEmployee(u))
        .filter((u) => !showActiveOnly || isActiveEmployee(u))
        .filter((u) => !selectedPsaInstances || selectedPsaInstances.length === 0 ||
          _.some(u.psaInstances, (instance) => _.includes(selectedPsaInstances, instance)))
        .value()
    );
  }, [isSynchronized]);

  const loadEmployees = useCallback(async () => {
    try {
      setLoading(true);
      const employeesData = await getEmployees();
      const employees = employeesData.map((e) => ({
        id: e.a,
        termDate: e.b,
        email: e.c,
        fullName: e.d,
        title: e.e,
        level: e.f,
        levelText: e.g,
        levelSource: e.h,
        startDate: e.i,
        practiceName: e.j,
        capability: e.k,
        locationName: e.l,
        employment_status: e.m,
        contractorRate: e.n,
        careerCoach: e.o,
        manager: e.p,
        statusHistory: e.q
          ? e.q.split("~").map((status) => {
            const [statusType, statusValue, comment] = status.split("^");
            return {
              statusType,
              status: statusValue,
              comment,
            };
          })
          : [],
        ptoPolicy: e.r,
        ptoModifier: e.s,
        ptoHoursOverride: e.t,
        resourceType: e.u,
        psaInstances: e.v,
        primaryPsaInstance: e.w
      }));
      setEmployees(employees);
      filterEmployees(
        employees,
        searchText,
        showNotSyncOnly,
        showFutureEmployeesOnly
      );
    } catch (e) {
      console.error(`Could not get employees`);
    } finally {
      setLoading(false);
      try {
        // Wait until employees are loaded, to then fetch Employee Manage Data
        getEmployeeManageData().then(data => {
          data.managers = data.employees.filter(e => e.l >= 3);
          if (data) setManageData(data);
        })
      } catch (e) {
        console.error(`Could not get employee manage data`);
      }
    }
  }, [filterEmployees, searchText, showFutureEmployeesOnly, showNotSyncOnly]);


  const search = (text) => {
    setSearchText(text);
    filterEmployees(employees, text, showNotSyncOnly, showFutureEmployeesOnly, showActiveOnly, selectedPsaInstances);
  };

  const updateShowNotSyncOnly = (enabled) => {
    setShowNotSyncOnly(enabled);
    filterEmployees(employees, searchText, enabled, showFutureEmployeesOnly, showActiveOnly, selectedPsaInstances);
  };

  const updateShowFutureEmployeesOnly = (enabled) => {
    setShowFutureEmployeesOnly(enabled);
    filterEmployees(employees, searchText, showNotSyncOnly, enabled, showActiveOnly, selectedPsaInstances);
  };

  const updateShowActiveOnly = (enabled) => {
    setShowActiveOnly(enabled);
    filterEmployees(employees, searchText, showNotSyncOnly, showFutureEmployeesOnly, enabled, selectedPsaInstances);
  }

  const updatePsaSelected = (instances) => {
    setSelectedPsaInstances(instances);
    filterEmployees(employees, searchText, showNotSyncOnly, showFutureEmployeesOnly, showActiveOnly, instances);
  }

  const handleRowClick = (record) => {
    setSelectedEmployee(record);
    setIsModalVisible(true);
  };

  const closeModal = () => {
    setSelectedEmployee(null);
    setIsModalVisible(false);
  };

  useEffect(() => {
    loadEmployees();
// eslint-disable-next-line
  }, []);

  return (
    <>
      <Row>
        <Col span={12}>
          <PageHeader
            title="Employees"
            subTitle={`(${filteredEmployees.length} shown)`}
            style={{padding: 0}}
          />
        </Col>
      </Row>

      <Row style={{marginBottom: 10}}>
        <Col span={4} style={{marginRight: 10}}>
          <Input
            placeholder="Search by name, email, or title"
            prefix={<SearchOutlined/>}
            onChange={(e) => search(e.target.value)}
            allowClear
          />
        </Col>
        <Col span={2} style={{marginRight: 10}}>
          <Select
            maxTagCount={0}
            maxTagPlaceholder={(omittedValues) => `${omittedValues.length} selected`}
            style={{width: "100%"}}
            mode="multiple"
            onChange={updatePsaSelected}
            placeholder="Psa Instance(s)"
            allowClear={true}
            showArrow={true}
            options={PSA_INSTANCE}
          />
        </Col>
        <Col>
          <Checkbox
            checked={showNotSyncOnly}
            onChange={(e) => updateShowNotSyncOnly(e.target.checked)}
          >
            Show Out of Sync Only
          </Checkbox>
        </Col>
        <Col>
          <Checkbox
            checked={showFutureEmployeesOnly}
            onChange={(e) => updateShowFutureEmployeesOnly(e.target.checked)}
          >
            Show Future Employees Only
          </Checkbox>
          <Checkbox
            checked={showContractorRates}
            onChange={(e) => setShowContractorRates(e.target.checked)}
          >
            Show Contractor Rates
          </Checkbox>
          <Checkbox
            checked={showActiveOnly}
            onChange={(e) => updateShowActiveOnly(e.target.checked)}
          >
            Show Active Only
          </Checkbox>
        </Col>
        <Col flex="auto">
          <div style={{display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8}}>
            <Tooltip title="Create New Employee">
              <Button
                icon={<PlusOutlined/>}
                onClick={() => {
                  setEditEmployee(null);
                  setCreateModalVisible(true);
                }}
              >
                Create New Employee
              </Button>
            </Tooltip>
            <Tooltip title="Refresh">
              <Button
                onClick={() => loadEmployees()}
                loading={loading}
                icon={<ReloadOutlined/>}
              >
                Refresh
              </Button>
            </Tooltip>
          </div>
        </Col>
      </Row>

      <Table
        ref={tableContainerRef}
        rowKey="email"
        dataSource={filteredEmployees}
        columns={columns}
        pagination={getPaginationConfig(filteredEmployees.length)}
        size="small"
        scroll={{x: "100%", y: tableHeight}}
        loading={loading}
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: {cursor: "pointer"},
        })}
      />
      {selectedEmployee && (
        <EmployeeFullInfoModal
          visible={isModalVisible}
          onClose={closeModal}
          employee={selectedEmployee}
        />
      )}
      {editEmployee && (
        <CreateUpdateEmployeeModal
          key={"edit-employee-modal"}
          visible={editModalVisible}
          onClose={closeEditModal}
          manageData={manageData}
          employee={editEmployee}
          onSave={handleUpsertEmployee}
          employees={employees}
        />
      )}
      <CreateUpdateEmployeeModal
        key={"create-employee-modal"}
        visible={createModalVisible}
        manageData={manageData}
        onClose={() => setCreateModalVisible(false)}
        onSave={handleUpsertEmployee}
      />
    </>
  );
};

export {Employees};
